import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { environment } from '@environment';
import {
  FindSupportChatsOpts,
  FollowUpEmailMode,
  SupportChatInList,
  SupportChatMarker,
  SupportChatsOrder,
  SupportingCompanyAccessLevel,
  User,
  ROLES_KEYS,
} from '@interfaces';
import { SupportChatFacade } from '@state/support-chat';
import { debounce } from 'lodash-es';
import { ReplaySubject, map, take, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { FollowUpEmailModalComponent } from '@management-components/follow-up-email-modal/follow-up-email-modal.component';
import { FollowUpChatModalComponent } from '@management-components/follow-up-chat-drawer/follow-up-chat-modal.component';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { ConfirmationModalService } from '@shared/confirmation-modal/confirmation-modal.service';
import { supportChatFilters } from '@shared/animations/support-chat-filters';
import {
  ContextMenuItemConfig,
  SUPPORT_CHAT_CONTEXT_MENU_ITEMS,
} from '@shared/support-chat/support-chat-context-menu.config';
import { UserAuthFacade } from '@state/user-auth';

@Component({
  selector: 'app-support-chat-mobile',
  templateUrl: './support-chat-mobile.component.html',
  styleUrls: ['./support-chat-mobile.component.scss'],
  animations: [supportChatFilters],
  standalone: false,
})
export class SupportChatMobileComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly supportChatFacade = inject(SupportChatFacade);
  private readonly stackedMessagesService = inject(StackedMessagesService);
  private readonly confirmationModal = inject(ConfirmationModalService);
  public dialog = inject(MatDialog);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @ViewChild('chatsList') chatsList: ElementRef<HTMLElement>;

  @Output() openSupportChatEventEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() closeSupportChatEventEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() searchTypeChange = new EventEmitter<ROLES_KEYS.Foreigner | ROLES_KEYS.Employer>();

  public myself$ = this.userAuthFacade.myself$;
  public foreignersSupportChatsList$ = this.supportChatFacade.foreignersSupportChatsList$.pipe(
    map(hugeObject => {
      return Object.values(hugeObject);
    })
  );
  public employersSupportChatsList$ = this.supportChatFacade.employersSupportChatsList$.pipe(
    map(hugeObject => {
      return Object.values(hugeObject);
    })
  );

  public openSupportChat$ = this.supportChatFacade.openSupportChat$;

  public showFiltersListDrawer = false;

  public loadingChatsList$ = this.supportChatFacade.loadingChatsList$;
  public loadingMoreChats$ = this.supportChatFacade.loadingMoreChats$;
  public loadingTranscriptionChat$ = this.supportChatFacade.loadingTranscriptionChat$;

  public myUnreadCountForeigners$ = this.supportChatFacade.myUnreadCountForeigners$;
  public myUnreadCountEmployers$ = this.supportChatFacade.myUnreadCountEmployers$;

  public pagination$ = this.supportChatFacade.pagination$;

  public myself: User;

  public debouncedSearch = debounce(opts => this.searchSupportChats(opts), 400);

  public SupportChatsOrder = SupportChatsOrder;
  public SupportChatMarker = SupportChatMarker;
  public SupportingCompanyAccessLevel = SupportingCompanyAccessLevel;
  public showMarkerButtonFor: string = null;
  public API_URL = environment.API_URL;

  public allChatsLoaded = false;

  public isChatFiltersVisible = true;

  private debouncedOnscroll = debounce(({ target, srcElement }) => {
    const { clientHeight } = srcElement as HTMLElement;
    const { scrollTop, scrollHeight } = target as HTMLElement;
    const shouldLoadMore = scrollHeight - scrollTop < clientHeight + 100;

    if (shouldLoadMore) {
      this.loadMoreSupportChats();
    }
  }, 150);

  public availableMenuItems: ContextMenuItemConfig[] = [];
  public actionHandlers: Record<string, (chat: SupportChatInList) => void> = {
    markAttention: chat => this.addSupportChatMarker(SupportChatMarker.ATTENTION_REQUIRED, chat.id),
    markLater: chat => this.addSupportChatMarker(SupportChatMarker.COMEBACK_LATER, chat.id),
    removeMarker: chat => this.removeSupportChatMarker(chat.id),
    markAsUnread: chat => this.markAsUnread(chat.id),
    followUpChat: chat => this.openFollowupDialogChat(chat),
    followUpEmail: chat => this.openFollowupDialogEmail(chat),
    getTranscription: chat => this.getTranscription(chat),
    none: () => {},
  };

  protected readonly ROLES_KEYS = ROLES_KEYS;

  public ngOnInit(): void {
    this.myself$.pipe(take(1), takeUntil(this.destroy$)).subscribe(myself => {
      this.myself = myself;
      this.loadMenuItems();
    });

    this.pagination$.pipe(takeUntil(this.destroy$)).subscribe(pagi => {
      this.allChatsLoaded = pagi.count === 0 || pagi.count >= pagi.all;
    });

    this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
      this.supportChatFacade.searchSupportChats({
        ...pagination,
        onlyUsersWithRole: ROLES_KEYS.Foreigner,
      });
    });

    this.loadingChatsList$.pipe(takeUntil(this.destroy$)).subscribe(isLoading => {
      if (!isLoading && this.chatsList?.nativeElement) {
        this.chatsList.nativeElement.scrollTop = 0;
      }
    });

    this.searchSupportChats({});
  }

  public ngAfterViewInit(): void {
    this.chatsList?.nativeElement.addEventListener('scroll', this.debouncedOnscroll);
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.chatsList?.nativeElement.removeAllListeners('scroll');
  }

  public searchSupportChats(providedOpts: Partial<FindSupportChatsOpts>): void {
    this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
      const opts: FindSupportChatsOpts = {
        ...pagination,
        ...providedOpts,
        offset: 0,
        limit: 25,
      };

      this.supportChatFacade.searchSupportChats(opts);
    });
  }

  public loadMoreSupportChats(): void {
    this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
      // we've loaded all of the notifications
      if (pagination.count >= pagination.all) {
        this.allChatsLoaded = true;
        return;
      }

      const opts: FindSupportChatsOpts = {
        ...pagination,
        offset: pagination.offset + 25,
        limit: 25,
      };
      this.supportChatFacade.loadMoreSupportChats(opts);
    });
  }

  public addSupportChatMarker(marker: SupportChatMarker, supportChatId: string): void {
    this.supportChatFacade.addSupportChatMarker({ marker, supportChatId });
  }

  public removeSupportChatMarker(supportChatId: string): void {
    this.supportChatFacade.removeSupportChatMarker(supportChatId);
  }

  public markAsUnread(supportChatId: string): void {
    this.supportChatFacade.markSupportChatAsUnread(supportChatId);
  }

  public openFollowupDialogChat(supportChat: SupportChatInList): void {
    this.dialog.open(FollowUpChatModalComponent, {
      height: 'calc(var(--app-visual-viewport-height, 100vh) - 24px)',
      width: '100vw',
      maxWidth: '100vw',
      data: { supportChat },
    });
  }

  public openFollowupDialogEmail(supportChat: SupportChatInList): void {
    this.dialog.open(FollowUpEmailModalComponent, {
      height: 'calc(var(--app-visual-viewport-height, 100vh) - 24px)',
      width: '100vw',
      maxWidth: '100vw',
      data: { users: [supportChat.supportedUser], mode: FollowUpEmailMode.SUPPORT_CHAT },
    });
  }

  public getTranscription(supportChat: SupportChatInList): void {
    this.confirmationModal
      .open({
        confirmationToTranslate: 'MANAGEMENT.CHAT.GET_TRANSCRIPTION_MODAL',
        translateParams: { userName: supportChat.supportedUser.name },
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(res => {
        if (!res) {
          return;
        }

        this.supportChatFacade.getTranscriptionSuccess$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
          this.stackedMessagesService.showInfo({ message: 'MANAGEMENT.CHAT.GET_TRANSCRIPTION_SUCCESS' });
        });

        this.supportChatFacade.getTranscription(supportChat.id);
      });
  }

  public openSupportChat(supportChatToOpen: SupportChatInList): void {
    this.openSupportChat$.pipe(take(1), takeUntil(this.destroy$)).subscribe(currentlyOpennedSupportChat => {
      this.openSupportChatEventEmitter.emit();

      // If no currentlu open support chat - open new and nothing else
      if (!currentlyOpennedSupportChat) {
        this.supportChatFacade.openSupportChat(supportChatToOpen.id);
        return;
      }

      // if currenlty some chat openned - close it succesfully and only then open new one
      this.supportChatFacade.closeSupportChatSuccess$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
        this.supportChatFacade.openSupportChat(supportChatToOpen.id);
      });

      this.supportChatFacade.closeSupportChat(currentlyOpennedSupportChat.supportChat.id);
    });
  }

  public closeChatList(): void {
    this.closeSupportChatEventEmitter.emit();
  }

  private loadMenuItems(): void {
    this.availableMenuItems = SUPPORT_CHAT_CONTEXT_MENU_ITEMS.filter(
      item => !item.permissionKey || this.hasPermission(item.permissionKey)
    );
  }

  public hasPermission(key: string): boolean {
    return this.myself.role.permissions?.some(permission => permission.key === key);
  }

  public tabChanged(selectedTabIndex?: number): void {
    if (selectedTabIndex === 0) {
      this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
        this.searchTypeChange.emit(ROLES_KEYS.Foreigner);

        this.supportChatFacade.searchSupportChats({
          ...pagination,
          offset: 0,
          onlyAssignedToMe: true,
          onlyUsersWithRole: ROLES_KEYS.Foreigner,
          orderBy: SupportChatsOrder.LAST_MESSAGE_DATE,
        });
      });
    }

    if (selectedTabIndex === 1) {
      this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
        this.searchTypeChange.emit(ROLES_KEYS.Employer);

        this.supportChatFacade.searchSupportChats({
          ...pagination,
          offset: 0,
          onlyAssignedToMe: false,
          onlyUsersWithRole: ROLES_KEYS.Employer,
          orderBy: SupportChatsOrder.LAST_MESSAGE_DATE,
        });
      });
    }
  }
}
